<template>
    <div v-show="isVisible" :class="variant === 'inline' ? 'w-auto max-w-[min(100%,240px)]' : 'w-full max-w-[450px] md:max-w-none lg:max-w-[450px]'">
        <button
            type="button"
            class="btn flex items-center justify-center gap-2 rounded-[2px] p-0! underline decoration-from-font underline-offset-4"
            :class="variant === 'inline' ? 'text-sm leading-tight' : 'w-full !px-8'"
            :aria-label="buttonAriaLabel"
            @click="openFasletWidget">
            <NuxtImg
                :class="props.variant === 'inline' ? '-mt-0.5 size-4 shrink-0' : 'size-5 shrink-0'"
                src="/icons/maatlint.svg"
                alt=""
                aria-hidden="true" />
            <span aria-live="polite">{{ buttonLabel }}</span>
        </button>
    </div>
</template>

<script setup lang="ts">
    const props = withDefaults(
        defineProps<{
            /** `inline`: next to e.g. "Kies je maat"; `default`: full-width block (e.g. under add-to-cart). */
            variant?: 'default' | 'inline';
        }>(),
        { variant: 'default' },
    );

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const isVisible = ref(false);
    const recommendedLabel = ref<string | null>(null);

    const defaultCta = computed(() => snippets['headless.pdp.fasletCtaDefault'] ?? 'Wat is mijn maat?');
    const recommendedTemplate = computed(
        () => snippets['headless.pdp.fasletCtaRecommended'] ?? 'Aanbevolen maat: {label}',
    );

    const buttonLabel = computed(() => {
        if (recommendedLabel.value) {
            return recommendedTemplate.value.replace('{label}', recommendedLabel.value);
        }
        return defaultCta.value;
    });

    const buttonAriaLabel = computed(() => buttonLabel.value);

    function handleButtonShow() {
        isVisible.value = true;
    }

    function handleButtonHidden() {
        isVisible.value = false;
    }

    function handleResult(result: { label: string }, _resultType: 'auto' | 'result-screen') {
        recommendedLabel.value = result.label;
    }

    /** True when Faslet reports no stored measurements (e.g. after the user clears their data). */
    function isEmptyProfile(profile: unknown): boolean {
        if (profile == null) {
            return true;
        }
        if (typeof profile === 'string') {
            return profile.trim() === '';
        }
        if (typeof profile === 'object') {
            return Object.keys(profile as Record<string, unknown>).length === 0;
        }
        return false;
    }

    /**
     * Faslet fires this whenever the stored profile changes. When the user clears their data the profile
     * is emptied, so drop the recommendation and let the button fall back to the default snippet label.
     */
    function handleDataChanged(data: { profile?: unknown }) {
        if (isEmptyProfile(data?.profile)) {
            recommendedLabel.value = null;
        }
    }

    function openFasletWidget() {
        const w = window as Window & { _faslet?: { openWidget?: () => void } };
        w._faslet?.openWidget?.();
    }

    defineExpose({
        handleButtonShow,
        handleButtonHidden,
        handleResult,
        handleDataChanged,
    });
</script>
